/**
 *
 * TableCellActions
 *
 */

import {
  Box,
  Button,
  Divider,
  Typography,
  withStyles,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import HistoryIcon from "@material-ui/icons/History";
import MailIcon from "@material-ui/icons/Mail";
import RefreshIcon from "@material-ui/icons/Refresh";
import propTypes from "prop-types";
import React from "react";
import { ReactComponent as LinkIcon } from "../../Assets/Icons/link.svg";
import { ReactComponent as AddIcon } from "../../Assets/Images/add.svg";
import { ReactComponent as DeleteIcon } from "../../Assets/Images/delete.svg";
import { ReactComponent as EditIcon } from "../../Assets/Images/edit.svg";
import { ReactComponent as EyeIcon } from "../../Assets/Images/eye.svg";
import { ReactComponent as InviteIcon } from "../../Assets/Images/invite.svg";
import VerifiedStatusChip from "../VerifiedStatusChip/";

const ActionButton = withStyles({
  root: {
    "&:hover": { background: "transparent" },
    padding: "0px 0px",
    minWidth: "0px",
  },
})(Button);

const VerticalDivider = () => (
  <Divider orientation="vertical" flexItem style={{ margin: "0 6px" }} />
);

const TableActionCell = ({
  viewMethod,
  editMethod,
  deleteMethod,
  canView,
  canDelete,
  canEdit,
  canDownload,
  downloadUrl,
  downloadMethod,
  getKeyAccess,
  getKeyMethod,
  canInvite,
  inviteMethod,
  canInviteReviewers,
  inviteRevMethod,
  showDefault,
  onDefault,
  canRedirect,
  onRedirect,
  canAdd,
  addMethod,
  isAddText,
  addTitle,
  historyMethod,
  canViewHistory,
  isMail,
  inviteAlreadySent,
  inviteAsText,
}) => {
  // Build visible actions in order, then place a single divider between each.
  // Avoids the old pairwise conditions that double-rendered dividers for
  // view+delete (without edit) and similar combos.
  const actions = [];

  if (canDownload) {
    actions.push(
      downloadMethod ? (
        <ActionButton
          key="download"
          disableRipple
          onClick={downloadMethod}
          color="primary"
        >
          <GetAppIcon style={{ marginRight: 4 }} />
          DOWNLOAD
        </ActionButton>
      ) : (
        <ActionButton
          key="download"
          disableRipple
          component="a"
          color="primary"
          href={downloadUrl}
          target="_blank"
        >
          DOWNLOAD
        </ActionButton>
      ),
    );
  }

  if (canAdd) {
    actions.push(
      <ActionButton
        key="add"
        disableRipple
        onClick={addMethod}
        style={{ minWidth: isAddText ? `125px` : `0px` }}
      >
        {isAddText && (
          <Typography component={`h3`} style={{ marginRight: 7 }}>
            {addTitle}
          </Typography>
        )}
        <AddIcon />
      </ActionButton>,
    );
  }

  if (canView) {
    actions.push(
      <ActionButton
        key="view"
        disableRipple
        onClick={(e) => {
          e.stopPropagation();
          viewMethod(e);
        }}
      >
        <EyeIcon />
      </ActionButton>,
    );
  }

  if (canInviteReviewers) {
    actions.push(
      <ActionButton
        key="invite-reviewers"
        disableRipple
        color="primary"
        onClick={() => inviteRevMethod()}
      >
        Invite to review
      </ActionButton>,
    );
  }

  if (canInvite) {
    const inviteLabel = inviteAlreadySent
      ? "Resend Family app invite"
      : "Family app invite";
    actions.push(
      <ActionButton
        key="invite"
        disableRipple
        color="primary"
        title={inviteLabel}
        aria-label={inviteLabel}
        onClick={(e) => {
          e.stopPropagation();
          inviteMethod(e);
        }}
        style={
          inviteAsText
            ? { minWidth: "auto", padding: "0 4px", whiteSpace: "nowrap" }
            : undefined
        }
      >
        {inviteAsText ? (
          <Typography
            component="span"
            style={{
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "Mulish",
              color: "inherit",
              textTransform: "none",
            }}
          >
            {inviteLabel}
          </Typography>
        ) : inviteAlreadySent ? (
          <RefreshIcon color="secondary" fontSize="small" />
        ) : isMail ? (
          <MailIcon color="secondary" />
        ) : (
          <InviteIcon />
        )}
      </ActionButton>,
    );
  }

  if (canEdit) {
    actions.push(
      <ActionButton
        key="edit"
        disableRipple
        color="primary"
        onClick={(e) => {
          e.stopPropagation();
          editMethod(e);
        }}
      >
        <EditIcon />
      </ActionButton>,
    );
  }

  if (canDelete) {
    actions.push(
      <ActionButton
        key="delete"
        disableRipple
        color="secondary"
        onClick={(e) => {
          e.stopPropagation();
          deleteMethod(e);
        }}
      >
        <DeleteIcon />
      </ActionButton>,
    );
  }

  if (canRedirect) {
    actions.push(
      <ActionButton
        key="redirect"
        disableRipple
        color="secondary"
        onClick={(e) => {
          e.stopPropagation();
          onRedirect(e);
        }}
      >
        <LinkIcon width={16} height={16} />
      </ActionButton>,
    );
  }

  if (canViewHistory) {
    actions.push(
      <ActionButton
        key="history"
        disableRipple
        color="secondary"
        onClick={(e) => {
          e.stopPropagation();
          historyMethod(e);
        }}
      >
        <HistoryIcon />
      </ActionButton>,
    );
  }

  return (
    <Box display="flex" alignItems="center">
      {actions.map((action, index) => (
        <React.Fragment key={action.key || index}>
          {index > 0 ? <VerticalDivider /> : null}
          {action}
        </React.Fragment>
      ))}
      {showDefault && (
        <span onClick={onDefault} style={{ cursor: "pointer", marginLeft: 5 }}>
          <VerifiedStatusChip
            data-status="light"
            label="Set as Default"
            style={{ cursor: "pointer" }}
          />
        </span>
      )}
    </Box>
  );
};

TableActionCell.propTypes = {
  downloadUrl: propTypes.string,
  viewMethod: propTypes.func,
  editMethod: propTypes.func,
  deleteMethod: propTypes.func,
  canView: propTypes.bool,
  canDelete: propTypes.bool,
  canEdit: propTypes.bool,
  canDownload: propTypes.bool,
  getKey: propTypes.bool,
  getKeyMethod: propTypes.bool,
  inviteAlreadySent: propTypes.bool,
  inviteAsText: propTypes.bool,
};
export default TableActionCell;
